import axios from "axios";
import { getSubdomain, getServerEndpoint } from "./utils";
import { getPersistentItem, removePersistentItem, setPersistentItem } from "./storage";

import { readSession, saveSession, clearSession, getSessionToken, sessionHeaders } from './sessionStorage';

const COLLA = getSubdomain();
const url = getServerEndpoint(COLLA);

// Create an Axios instance with the base URL
const api = axios.create({
  baseURL: url,
});

export function getAuthToken() {
  return getPersistentItem("token") || "";
}

export async function loginUser(userId, password) {
  try {
    const response = await api.post("/auth/login", { userId, password });
    const token = response?.data?.token;
    if (token) {
      // Keep cookie in sync so auth still works if localStorage quota is full.
      setPersistentItem("token", token, { alwaysSyncCookie: true });
      clearSession();
      if (response.data.session && !saveSession(response.data.session)) return false;
      return true;
    } else {
      console.error("LOGIN ERROR: No token received");
      return false;
    }
  } catch (error) {
    console.error("LOGIN ERROR:", error?.response?.data);
    return false;
  }
}

// Whether a member can log in with a password, or makes one first (the app's
// «Contrasenya nova»). Without the sessions API (404), the old login. null: no account.
export async function accountStatus(userId) {
  try {
    const { data } = await api.get(`/auth/accounts/${encodeURIComponent(userId)}`);
    return { hasPassword: Boolean(data?.hasPassword), requiresCollaReset: Boolean(data?.requiresCollaReset) };
  } catch (error) {
    if (error?.response?.status === 404) return { hasPassword: true, requiresCollaReset: false };
    if ([401, 403].includes(error?.response?.status)) return null;
    throw error;
  }
}

// The first password of an account that has none, and its session
export async function registerUser(userId, password) {
  try {
    const { data } = await api.post('/auth/sessions/register', { userId, password });
    if (!data?.token) return false;
    clearSession();
    return Boolean(saveSession(data));
  } catch (error) {
    console.error("REGISTER ERROR:", error?.response?.data);
    return false;
  }
}

export function logoutUser() {
  if (getSessionToken()) void api.post('/auth/sessions/logout', {}, { headers: sessionHeaders() }).catch(() => {});
  clearSession();
  const token = getPersistentItem("token");

  if (token) {
    removePersistentItem("token");
    window.location.reload();
    return true;
  }
}

export async function isLoggedIn() {
  const session = readSession();
  if (session) {
    try {
      const { data } = await api.get('/auth/sessions/me', { headers: sessionHeaders() });
      if (data.expiresAt - Date.now() < 30 * 86400000) {
        const renewed = await api.post('/auth/sessions/renew', {}, { headers: sessionHeaders() });
        saveSession({ ...session, ...renewed.data });
      }
      return { isLogged: true, userId: data.actor.userId };
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) {
        clearSession();
        return { isLogged: false, userId: -1 };
      }
      return { isLogged: true, userId: session.actor.userId };
    }
  }
  try {
    const { data } = await api.get('/auth/preparation');
    if (data.passwordAuthRequired) return { isLogged: false, userId: -1 };
  } catch { /* A temporary failure must not erase the existing login. */ }
  const token = getPersistentItem("token");

  if (token) {
    try {
      const response = await api.post("/auth/isLoggedIn", { token });

      return {
        isLogged: !!response.data.isLoggedIn,
        userId: response.data.userId,
      }
    } catch (error) {
      const is401 = error?.response?.status === 401;
      if (is401) {
        removePersistentItem("token");
        return { isLogged: false, userId: -1 };
      }

      // Network error: trust the local token
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return { isLogged: true, userId: payload.id };
      } catch {
        return { isLogged: false, userId: -1 };
      }
    }
  }

  return {
    isLogged: false,
    userId: -1,
  }
}
